import React, { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import BannerCarousel from '../components/BannerCarousel';
import CategoryShowcase from '../components/CategoryShowcase';
import DiscountImageSection from '../components/DiscountImageSection';
import BestSellingProducts from '../components/BestSellingProducts';
import ProductVideos from '../components/ProductVideos';
import LoveByCelebs from '../components/LoveByCelebs';
import CustomerReviews from '../components/CustomerReviews';
import LoadingSpinner from '../components/LoadingSpinner';

const DEFAULT_REVIEWS_DISPLAY_COUNT = 6;

const MarketingHomePage = () => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [discountImage, setDiscountImage] = useState(null);
  const [newArrivalImage, setNewArrivalImage] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [celebrities, setCelebrities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [carouselIntervalMs, setCarouselIntervalMs] = useState(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      const [bannersRes, categoriesRes, bestSellersRes, videosRes, celebritiesRes, reviewsRes, settingsRes, miscPhotosRes] = await Promise.all([
        apiFetch('/api/public/marketing/banners/?page_size=200'),
        apiFetch('/api/public/categories/?page_size=200&show_on_homepage=true'),
        apiFetch('/api/public/marketing/best-sellers/?page_size=200'),
        apiFetch('/api/public/marketing/product-videos/?page_size=200'),
        apiFetch('/api/public/marketing/celebrities/?page_size=200'),
        apiFetch('/api/public/marketing/reviews/?page_size=200'),
        apiFetch('/api/public/marketing/settings/'),
        apiFetch('/api/public/marketing/misc-photos/?page_size=200'),
      ]);
      for (const res of [bannersRes, categoriesRes, bestSellersRes, videosRes, celebritiesRes, reviewsRes]) {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
      }
      const bannersData = await bannersRes.json();
      const categoriesData = await categoriesRes.json();
      const bestSellersData = await bestSellersRes.json();
      const videosData = await videosRes.json();
      const celebritiesData = await celebritiesRes.json();
      const reviewsData = await reviewsRes.json();
      const bannerResults = Array.isArray(bannersData?.results) ? bannersData.results : (Array.isArray(bannersData) ? bannersData : []);
      const categoryResults = Array.isArray(categoriesData?.results) ? categoriesData.results : (Array.isArray(categoriesData) ? categoriesData : []);
      const bestSellerResults = Array.isArray(bestSellersData?.results) ? bestSellersData.results : (Array.isArray(bestSellersData) ? bestSellersData : []);
      const videoResults = Array.isArray(videosData?.results) ? videosData.results : (Array.isArray(videosData) ? videosData : []);
      const celebrityResults = Array.isArray(celebritiesData?.results) ? celebritiesData.results : (Array.isArray(celebritiesData) ? celebritiesData : []);
      const reviewResults = Array.isArray(reviewsData?.results) ? reviewsData.results : (Array.isArray(reviewsData) ? reviewsData : []);

      let discountImageResult = null;
      let newArrivalImageResult = null;
      if (miscPhotosRes.ok) {
        const miscPhotosData = await miscPhotosRes.json();
        const miscPhotoResults = Array.isArray(miscPhotosData?.results) ? miscPhotosData.results : (Array.isArray(miscPhotosData) ? miscPhotosData : []);
        discountImageResult = miscPhotoResults.find(p => p.key === 'discount_image') || null;
        newArrivalImageResult = miscPhotoResults.find(p => p.key === 'new_arrival_image') || null;
      }

      let intervalMs;
      let reviewsDisplayCount = DEFAULT_REVIEWS_DISPLAY_COUNT;
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        intervalMs = settingsData?.carousel_interval_ms;
        reviewsDisplayCount = settingsData?.reviews_display_count || DEFAULT_REVIEWS_DISPLAY_COUNT;
      }

      return {
        bannerResults,
        categoryResults,
        bestSellerResults,
        videoResults,
        celebrityResults,
        reviewResults: reviewResults.slice(0, reviewsDisplayCount),
        intervalMs,
        discountImageResult,
        newArrivalImageResult,
      };
    };

    (async () => {
      let result;
      try {
        result = await loadData();
      } catch (_) {
        // Retry once after 1.5s to recover from cold-start / transient errors
        try {
          await new Promise(r => setTimeout(r, 1500));
          result = await loadData();
        } catch {
          result = null;
        }
      }

      if (!active) return;

      if (result) {
        setBanners(result.bannerResults);
        setCategories(result.categoryResults);
        setBestSellers(result.bestSellerResults);
        setVideos(result.videoResults);
        setCelebrities(result.celebrityResults);
        setReviews(result.reviewResults);
        setCarouselIntervalMs(result.intervalMs);
        setDiscountImage(result.discountImageResult);
        setNewArrivalImage(result.newArrivalImageResult);
      } else {
        setBanners([]);
        setCategories([]);
        setBestSellers([]);
        setVideos([]);
        setCelebrities([]);
        setReviews([]);
        setDiscountImage(null);
        setNewArrivalImage(null);
      }
      setLoading(false);
    })();

    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <BannerCarousel banners={banners} intervalMs={carouselIntervalMs} />
      <CategoryShowcase categories={categories} newArrivalPhoto={newArrivalImage} />
      <DiscountImageSection photo={discountImage} />
      <BestSellingProducts items={bestSellers} />
      <ProductVideos items={videos} />
      <LoveByCelebs celebrities={celebrities} />
      <CustomerReviews reviews={reviews} />
    </div>
  );
};

export default MarketingHomePage;
